import React, { useEffect, useState } from "react";
import { User } from "lucide-react";
import { usePaymentCalculations } from "./usePaymentCalculations";
import { PaymentSummaryContentProps } from "./types";
import { Checkbox, Divider } from "@mui/material";

const PaymentSummaryContent: React.FC<PaymentSummaryContentProps> = ({
  selectedProducts,
  paymentMethod,
  customerName,
  discount,
  tipsAmount,
  receivedAmount,
  onReceivedAmountChange,
  time,
  outstandingBalance = 0,
  isLoadingOutstanding = false,
}) => {
  const { subtotal, discountValue, total, remainingAmount } = usePaymentCalculations({
    selectedProducts,
    discount,
    tipsAmount,
    receivedAmount,
  });

  const [includeOutstanding, setIncludeOutstanding] = useState(false);
  const [localAmount, setLocalAmount] = useState(receivedAmount === "0" ? "" : receivedAmount);

  useEffect(() => {
    setLocalAmount(receivedAmount === "0" ? "" : receivedAmount);
  }, [receivedAmount]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e" || e.key === "+") e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("Text");
    if (parseFloat(pasted) < 0) e.preventDefault();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/-/g, "");
    if (/^\d*\.?\d*$/.test(val)) {
      if (val.startsWith("0") && val.length > 1 && !val.startsWith("0.")) {
        val = val.replace(/^0+/, "");
      }
      setLocalAmount(val);
      const numVal = parseFloat(val || "0");
      const maxTotal = includeOutstanding ? total + outstandingBalance : total;
      if (numVal > maxTotal) {
        onReceivedAmountChange(maxTotal.toFixed(2));
        setLocalAmount(maxTotal.toFixed(2));
      } else {
        onReceivedAmountChange(val === "" ? "0" : val);
      }
    }
  };

  const handleBlur = () => {
    if (localAmount === "") {
      setLocalAmount("0");
      onReceivedAmountChange("0");
    }
  };

  const isCashLike = paymentMethod === "Cash" || paymentMethod === "Card";
  const isBuyOnCredit = paymentMethod === "Buy on credit";

  const totalWithOutstanding = includeOutstanding ? total + outstandingBalance : total;
  const calculatedRemaining = totalWithOutstanding - parseFloat(localAmount || "0");
  const orderPriceData = Number(outstandingBalance) + Number(subtotal);

  const roundAmount = (value: any) => {
    return Number(value).toFixed(2);
  };

  return (
    <div className="text-left -pb-5">
      {/* Customer Info - matches PaymentSummary exactly */}
      <div className="flex items-center gap-3 bg-gray-100 p-3 px-5 mb-2 rounded-md">
        <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
          <User className="text-gray-600" size={22} />
        </div>
        <div className="flex flex-col text-sm">
          <span className="font-semibold">Customer: {customerName || "N/A"}</span>
          <span className="text-gray-600">Payment: {paymentMethod}</span>
          <span className="text-gray-500 text-xs">
            Date: {time.year}-{time.month}-{time.day} | {time.hours}:{time.minutes}
          </span>
        </div>
      </div>

      {/* Order Summary - matches PaymentSummary exactly */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-700 mb-1">Order Summary</h3>
        <Divider className="mb-1.5" />

        {/* Outstanding Balance - matches PaymentSummary exactly */}
        {!isBuyOnCredit && (
          <div className="flex items-center justify-between text-sm">
            <div>
              <Checkbox onChange={() => setIncludeOutstanding(!includeOutstanding)} checked={includeOutstanding} size="small" disabled={isLoadingOutstanding || outstandingBalance <= 0} />
              <span className="font-medium">Outstanding Balance</span>
            </div>
            <span className="font-medium">{isLoadingOutstanding ? <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse"></span> : `${roundAmount(outstandingBalance)} QAR`}</span>
          </div>
        )}

        <div className="space-y-2 text-sm">
          {/* Outstanding Balance for Buy on Credit - matches PaymentSummary exactly */}
          {isBuyOnCredit && (
            <div className="flex mt-2 justify-between">
              <span className="px-10">Outstanding Balance</span>
              <span className="font-medium">{isLoadingOutstanding ? <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse"></span> : `${roundAmount(outstandingBalance)} QAR`}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="px-10">Order Amount</span>
            <span className="font-medium">{roundAmount(subtotal)} QAR</span>
          </div>
          <div className="flex justify-between">
            <span className="px-10">Discount</span>
            <span>- {roundAmount(discountValue)} QAR</span>
          </div>
          <div className="flex justify-between">
            <span className="px-10">Final Amount</span>
            <span>{roundAmount(total)} QAR</span>
          </div>
        </div>
      </div>

      {/* Payment Status - matches PaymentSummary exactly */}
      <div className="mb-2">
        <h3 className="font-semibold text-gray-700 mb-1">Payment Status</h3>
        <Divider className="mb-2" />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="px-10 mt-2">Total Payable</span>
            <span className="font-medium mt-2">
              {isLoadingOutstanding ? <span className="inline-block w-20 h-4 bg-gray-200 rounded animate-pulse"></span> : `${roundAmount(totalWithOutstanding)} QAR`}
            </span>
          </div>

          {/* Received Amount - matches PaymentSummary exactly */}
          {isBuyOnCredit ? (
            <div className="flex justify-between">
              <span className="px-10">Received Amount</span>
              <span className="font-medium">0 QAR</span>
            </div>
          ) : (
            <div className="flex justify-between">
              <span className="px-10">Received Amount</span>
              <input
                type="text"
                value={localAmount}
                onChange={handleInputChange}
                onFocus={() => {
                  if (localAmount === "0") setLocalAmount("");
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="0"
                disabled={isLoadingOutstanding || orderPriceData <= 0}
                className="w-28 h-9 rounded-md border border-gray-300 px-2.5 text-right text-sm text-gray-900 focus:outline-none focus:border-blue-400"
              />
            </div>
          )}

          {/* Remaining Amount - matches PaymentSummary exactly */}
          <div className="flex justify-between">
            <span className="px-10">Remaining Amount</span>
            <span className="font-medium">
              {isLoadingOutstanding ? (
                <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse"></span>
              ) : (
                `${roundAmount(isCashLike ? calculatedRemaining : totalWithOutstanding - parseFloat(receivedAmount || "0"))} QAR`
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummaryContent;
